//@flow
import * as React from 'react';
import type { FpPjPlan } from 'common/models/internal/FpPjPlan';
export type EditVisitType = Array<{
    visitId: string,
    targetBeatId?: string,
    originalBeatId?: string
}>;

type PlanEditContextType = {
    edits?: EditVisitType,
    editHistory?: EditVisitType,
    addEdit: ({
        outletVisitId: string,
        targetBeatId: string,
        originalBeatId?: string
    }) => void,
    undoEdit: () => void,
    redoEdit: () => void,
    cleanEdits: () => void,
    saveEdits: () => void,
    maxEditLimitReached: boolean,
    editInProgress: boolean,
    ...
};

type Props = {
    clientId: string,
    plan: FpPjPlan,
    editVisit: (
        {
            clientId: string,
            planId: string,
            pjpId: string
        },
        edits: EditVisitType,
        dryRun?: boolean
    ) => Promise<any>,
    children: React.Node,
    pjpId: string,
    saveCallback: () => void
};

const MAX_EDITS_ALLOWED = 5;

export const PlanEditContext: React$Context<PlanEditContextType> = React.createContext(
    {}
);

const PlanEditProvider = ({
    children,
    editVisit,
    clientId,
    plan,
    pjpId,
    saveCallback
}: Props): React.Node => {
    const [edits, setEdits] = React.useState<?EditVisitType>(null);
    const [editHistory, setEditHistory] = React.useState<?EditVisitType>(null);
    const [editInProgress, setEditInProgress] = React.useState(false);

    const _fetchEditPreview = React.useCallback(
        (nextEdits, editHistory) => {
            const { id: beatPlanId } = plan.salesbeatPlan.beatPlanId;
            setEditInProgress(true);
            if (!edits) {
                setEdits([]);
            }
            // const metricsBeforeEdit = this.getMetricsBeforeEdits(plan);
            editVisit(
                { clientId, planId: beatPlanId, pjpId },
                nextEdits.map(({ visitId, targetBeatId }) => ({
                    visitId,
                    targetBeatId
                })),
                true
            )
                .then(() => {
                    setEdits(nextEdits);
                    if (editHistory) {
                        setEditHistory(editHistory);
                    }
                })
                .finally(() => {
                    setEditInProgress(false);
                });
        },
        [clientId, editVisit, edits, pjpId, plan]
    );

    const addEdit = React.useCallback(
        ({ outletVisitId, targetBeatId, originalBeatId }) => {
            const pastEditIndex = edits?.findIndex(
                ({ visitId }) => visitId === outletVisitId
            );
            let nextEdit = {
                visitId: outletVisitId,
                originalBeatId
            };
            if (targetBeatId) {
                nextEdit = {
                    ...nextEdit,
                    targetBeatId
                };
            }
            let nextEdits;
            if (
                pastEditIndex !== null &&
                pastEditIndex !== undefined &&
                pastEditIndex !== -1
            ) {
                nextEdits = [...(edits || [])];
                nextEdits.splice(pastEditIndex, 1);
                nextEdit.originalBeatId = edits[pastEditIndex].originalBeatId;
                if (edits[pastEditIndex].originalBeatId !== targetBeatId) {
                    // case when moving back to original beat
                    nextEdits = nextEdits.concat(nextEdit);
                }
            } else {
                nextEdits = (edits || []).concat(nextEdit);
            }

            _fetchEditPreview(nextEdits);
        },
        [_fetchEditPreview, edits]
    );

    const saveEdits = React.useCallback(() => {
        if (edits) {
            const { id: beatPlanId } = plan.salesbeatPlan.beatPlanId;
            setEditInProgress(true);
            editVisit(
                {
                    clientId,
                    pjpId,
                    planId: beatPlanId
                },
                edits,
                false
            ).then(() => {
                saveCallback();
                setEdits(null);
            });
        }
    }, [clientId, editVisit, edits, pjpId, plan, saveCallback]);

    const cleanEdits = React.useCallback(() => {
        setEdits(null);
        setEditHistory(null);
        saveCallback();
    }, [saveCallback]);

    const undoEdit = React.useCallback(() => {
        const nextEdits = [...(edits || [])];
        const nextEditHistory = (editHistory || []).concat(nextEdits.pop());
        _fetchEditPreview(nextEdits, nextEditHistory);
    }, [_fetchEditPreview, editHistory, edits]);

    const redoEdit = React.useCallback(() => {
        const nextEditHistory = [...(editHistory || [])];
        const nextEdits = (edits || []).concat(nextEditHistory.pop());
        _fetchEditPreview(nextEdits, nextEditHistory);
    }, [_fetchEditPreview, editHistory, edits]);

    return (
        <PlanEditContext.Provider
            value={{
                edits,
                addEdit,
                editInProgress,
                saveEdits,
                cleanEdits,
                undoEdit,
                redoEdit,
                maxEditLimitReached: (edits?.length || 0) >= MAX_EDITS_ALLOWED,
                editHistory
            }}
        >
            {children}
        </PlanEditContext.Provider>
    );
};

export default PlanEditProvider;
